import type { HTMLAttributes } from 'react';
import classNames from 'classnames';
import { Segment } from '@fxtr/i18n';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { Link } from '@/components/atoms/Link';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Col, Row } from '@/components/atoms/Grid';
import { Card } from '@/components/atoms/Card';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { carMakesListUK, carMakesListFR } from './fixtures';

import styles from './index.module.scss';

interface CarMakesListProps extends HTMLAttributes<HTMLDivElement> {
  readonly title: string;
  readonly viewAllLinkLabel: string;
  readonly viewAllLinkUrl: string;
  readonly segment: Segment;
  readonly showAllMakes?: boolean;
  readonly product?: string;
}

export function CarMakesList({
  title,
  viewAllLinkLabel,
  viewAllLinkUrl,
  showAllMakes = false,
  product,
  segment,
  className,
  ...props
}: CarMakesListProps): JSX.Element {
  const carMakesList = segment === Segment.Fixter_UK ? carMakesListUK : carMakesListFR;
  const makes = showAllMakes ? carMakesList : carMakesList.slice(0, 12);

  return (
    <ContentComponentSection className={styles.component}>
      <Row className={styles.CarMakesListWrapper}>
        <Col>
          <Card
            cornerRadius={4}
            className={classNames(CarMakesList.displayName, styles.CarMakesList, className)}
            {...props}
          >
            <HeadingLevels semanticLevel={3} styleLevel={3} className={styles.title}>
              {title}
            </HeadingLevels>
            <ul className={styles.listWrapper}>
              {makes.map(({ href, name }) => (
                <li key={href} className={classNames(styles.listItem)}>
                  <SimpleLink
                    className={styles.carLink}
                    href={`/${[product, href].filter(Boolean).join('/')}`}
                    text={name}
                  />
                </li>
              ))}
            </ul>
            {!showAllMakes && (
              <Link
                className={classNames(styles.showAllMakesBtn)}
                type="M"
                href={viewAllLinkUrl}
                purpleIcon
                iconId="arrows/arrow-up-right"
              >
                {viewAllLinkLabel}
              </Link>
            )}
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
}

CarMakesList.displayName = 'CarMakesList';
